import { Alert, AlertDescription, AlertTitle, Button, DropdownMenuItem } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { HelpPopover, PageHeader } from '@/design';
import { useCan } from '@/domains/auth';
import type { IEtagged } from '@/domains/api';
import type {
  IListField,
  IListFieldCatalogue,
  IListFormattingRule,
  IListOrder,
  IListQuery,
  IListScope,
  ISavedView,
  ListFieldSource
} from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { SectionTabs, LoadError, useAdminData, type IAdminTab } from '../../components/admin-page';
import { useListReread } from '../../components/list-area';
import {
  NO_PROBLEMS,
  criteriaPaths,
  isChanged,
  placeProblems,
  writeCriteria,
  type ICriteriaGroup,
  type ICriteriaWords,
  type IPlacedCriteriaProblems,
  type KindOf
} from '../../list-criteria';
import { DEFAULT_LIST_PAGE_SIZE, listSurveyLabel, visibilityLabel } from '../../lists';
import { NEW_REPORT, TEAM_PATHS, reportPath } from '../../paths';
import { MigratedChip, sayProblem, startOf } from '../lists/area';
import { ExportDialog } from '../lists/export-dialog';
import { SaveAsReportDialog, useViewSaves } from '../lists/save-view';
import { useCatalogueLookups, useCriteriaWords } from '../lists/use-catalogue';
import { VisibilityIcon, viewOwner } from '../lists/view-picker';
import { RunNowDialog, useReportRuns, type IReportRuns } from './runs';

/**
 * One report (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §4.2) —
 * Salesforce's report builder reduced to what the team used: criteria, columns, a sort, a grouping,
 * exported to Excel. A record beside the Reports list (`AdminReportsArea`), **keyed by the view**
 * (`AdminReportRecord`), so nothing of one report is drawn under another's address; a report not
 * saved yet is the record at `/team/reports/new?survey=<code>`, a segment no GUID spells.
 *
 * **The record holds the definition** — the filter, columns, sorts, grouping, scope and page size
 * as the reader changed them, *Save ▾* tinted while they differ from the report as saved — and
 * its **three routed tabs** read it: **Definition** edits it (`builder.tsx`), **Preview** asks
 * survey-api for its first page, **Runs** keeps the files (`runs.tsx`). So a tab change keeps every
 * change, and the address names the tab.
 *
 * **The header** (§4.2): *Run now…* (a saved report's — the run is of the report as saved),
 * *Export now…* (the export dialog over the screen's query — a file at once, nothing kept) and
 * *Save ▾* — the view's saves of Lists (`useViewSaves`, `kind: report`, its rules and refusals) and
 * *Open as a list* (Q7), with Lists' *From Salesforce* tag beside it on a migrated report. A report made by *Save as…* is opened at its own address; one deleted leaves
 * for the list, which says so.
 */
export interface IReportScreen {
  /** Null for a report not saved yet. */
  saved: Nullable<IEtagged<ISavedView>>;
  survey: string;
  catalogue: IListFieldCatalogue;
  fieldOf: (code: string) => IListField | undefined;
  sourceOf: (code: string) => ListFieldSource | undefined;
  kindOf: KindOf;
  words: ICriteriaWords;
  root: ICriteriaGroup;
  setRoot: (root: ICriteriaGroup) => void;
  columns: string[];
  setColumns: (columns: string[]) => void;
  sorts: IListOrder[];
  setSorts: (sorts: IListOrder[]) => void;
  groupBy: IListOrder[];
  setGroupBy: (groupBy: IListOrder[]) => void;
  scope: Nullable<IListScope>;
  setScope: (scope: Nullable<IListScope>) => void;
  pageSize: Nullable<number>;
  rules: IListFormattingRule[];
  /** The definition as the wire takes it — the screen's, unsaved changes included. */
  query: IListQuery;
  /** A save's refusals of the criteria, on the builder's rows. */
  placed: IPlacedCriteriaProblems;
  runs: IReportRuns;
}

const ReportContext = React.createContext<Nullable<IReportScreen>>(null);

/** The report the tabs read — inside `AdminReportRecord` alone. */
export function useReport(): IReportScreen {
  const report = React.useContext(ReportContext);
  if (report === null) throw new Error('useReport outside a report record');
  return report;
}

/** The route element of `/team/reports/:viewId` — remounted per report. */
export function AdminReportRecord() {
  const { viewId = '' } = useParams();
  const location = useLocation();
  const fresh = viewId === NEW_REPORT;
  const survey = new URLSearchParams(location.search).get('survey') || 'FA';
  return (
    <ReportOpening
      key={fresh ? `${NEW_REPORT}:${survey}` : viewId}
      viewId={fresh ? null : viewId}
      survey={survey}
    />
  );
}

type Opened =
  | { kind: 'open'; view: Nullable<IEtagged<ISavedView>>; survey: string; catalogue: Nullable<IListFieldCatalogue> }
  | { kind: 'missing' }
  | { kind: 'list'; view: ISavedView };

/** The report's read — `GET /views/{id}`, which stamps the open — then its survey's catalogue. */
function ReportOpening({ viewId, survey }: { viewId: Nullable<string>; survey: string }) {
  const { data, error } = useAdminData<Opened>(async (signal) => {
    if (viewId === null) {
      return { kind: 'open', view: null, survey, catalogue: await listsApi.fields(survey, signal) };
    }
    const view = await listsApi.openView(viewId, signal);
    if (view === null) return { kind: 'missing' };
    if (view.body.kind !== 'report') return { kind: 'list', view: view.body };
    const own = view.body.query.survey;
    return { kind: 'open', view, survey: own, catalogue: await listsApi.fields(own, signal) };
  });

  const plain = (body: React.ReactNode) => (
    <>
      <PageHeader title="Report" crumb="Reports" />
      <div className="grid gap-4 px-6 py-6">{body}</div>
    </>
  );
  if (error) return plain(<LoadError message={`This report could not be opened. ${error}`} />);
  if (data === null) return plain(<p className="text-sm text-muted-foreground">Opening the report…</p>);
  if (data.kind === 'missing') {
    return plain(
      <Alert>
        <AlertTitle>No such report</AlertTitle>
        <AlertDescription>It was deleted, or it is another account’s private report.</AlertDescription>
      </Alert>
    );
  }
  if (data.kind === 'list') {
    return plain(
      <Alert>
        <AlertTitle>{data.view.name} is a list, not a report</AlertTitle>
        <AlertDescription>
          <Link
            className="underline"
            to={`${TEAM_PATHS.lists}?${new URLSearchParams({ survey: data.view.query.survey, view: data.view.id })}`}
          >
            Open it on Lists
          </Link>
        </AlertDescription>
      </Alert>
    );
  }
  if (data.catalogue === null) {
    return plain(<p className="text-sm text-muted-foreground">This survey is not on this deployment.</p>);
  }
  return <ReportScreen opened={data.view} survey={data.survey} catalogue={data.catalogue} />;
}

function ReportScreen({
  opened,
  survey,
  catalogue
}: {
  opened: Nullable<IEtagged<ISavedView>>;
  survey: string;
  catalogue: IListFieldCatalogue;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const rereadList = useListReread();
  const can = useCan();
  const [saved, setSaved] = React.useState(opened);
  const view = saved?.body ?? null;

  const { fields, fieldOf, sourceOf, kindOf, words: lookupWords } = useCatalogueLookups(catalogue);

  const [start] = React.useState(() => startOf(view, fields, survey));
  const [root, setRoot] = React.useState<ICriteriaGroup>(start.root);
  const words = useCriteriaWords(survey, root, lookupWords);
  const [columns, setColumns] = React.useState<string[]>(start.columns);
  const [sorts, setSorts] = React.useState<IListOrder[]>(start.sorts);
  const [groupBy, setGroupBy] = React.useState<IListOrder[]>(start.groupBy);
  const [scope, setScope] = React.useState<Nullable<IListScope>>(start.scope);
  const [pageSize] = React.useState<Nullable<number>>(start.pageSize);
  const [rules] = React.useState<IListFormattingRule[]>(start.rules);
  const [running, setRunning] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [bridging, setBridging] = React.useState(false);

  const query: IListQuery = {
    survey,
    scope,
    criteria: writeCriteria(root, kindOf),
    columns,
    sorts,
    groupBy
  };
  const changed = view !== null && isChanged(view, { query, pageSize, formattingRules: rules });
  // A saved report's file is named after it (`viewId`); every member is the screen's, the scope
  // too — an empty one rather than null, which would read the saved report's (SR-418).
  const exportQuery: IListQuery = view === null ? query : { ...query, viewId: view.id, scope: scope ?? {} };
  const runs = useReportRuns(view?.id ?? null);
  const viewId = view?.id ?? null;
  const tabSearch = viewId === null ? location.search : '';

  const viewSaves = useViewSaves({
    kind: 'report',
    survey,
    saved,
    changed,
    screen: () => ({ query, pageSize, formattingRules: rules }),
    pageSize: pageSize ?? DEFAULT_LIST_PAGE_SIZE,
    canShare: can('lists.share'),
    sayProblem: (problem) => sayProblem(problem, query, fieldOf),
    menuLabel: 'Save',
    // As Lists (SR-412): a report changed, or a new one not saved yet, says it on *Save ▾*.
    unsaved: changed || view === null,
    moreItems: <DropdownMenuItem onSelect={() => setBridging(true)}>Open as a list</DropdownMenuItem>,
    onAdopt: (next) => {
      setSaved(next);
      rereadList();
    },
    onCreated: (created) => {
      rereadList();
      navigate(reportPath(created.body.id), { replace: viewId === null, state: location.state });
    },
    onDeleted: () => {
      rereadList();
      navigate(`${TEAM_PATHS.reports}?${new URLSearchParams({ survey })}`, {
        state: { deletedReport: view?.name ?? '' }
      });
    }
  });

  const criteriaProblems = viewSaves.problems.filter((problem) => problem.target.startsWith('query.criteria'));
  const placed =
    criteriaProblems.length > 0 ? placeProblems(criteriaProblems, root, criteriaPaths(root)) : NO_PROBLEMS;

  const screen: IReportScreen = {
    saved,
    survey,
    catalogue,
    fieldOf,
    sourceOf,
    kindOf,
    words,
    root,
    setRoot,
    columns,
    setColumns,
    sorts,
    setSorts,
    groupBy,
    setGroupBy,
    scope,
    setScope,
    pageSize,
    rules,
    query,
    placed,
    runs
  };

  const tabs: IAdminTab[] = [
    { to: reportPath(viewId ?? NEW_REPORT, 'definition', tabSearch), label: 'Definition', end: true },
    { to: reportPath(viewId ?? NEW_REPORT, 'preview', tabSearch), label: 'Preview' },
    ...(viewId !== null ? [{ to: reportPath(viewId, 'runs'), label: 'Runs' }] : [])
  ];
  const runReason = React.useId();

  return (
    <ReportContext.Provider value={screen}>
      <PageHeader
        title={`${changed ? '* ' : ''}${view?.name ?? 'New report'}`}
        documentTitle={view?.name ?? 'New report'}
        crumb={`Reports · ${listSurveyLabel(survey)}`}
        meta={<ReportFacts view={view} />}
        tabs={<SectionTabs tabs={tabs} />}
        actions={
          <>
            <HelpPopover label="About running and exporting a report" align="start">
              <p>
                <strong>Run now</strong> writes every row of the report as saved to an Excel file kept on the Runs tab,
                with who ran it, when and a note — download it there, any time, as often as needed. It runs in the
                background: you can leave the page.
              </p>
              <p>
                <strong>Export now</strong> downloads the report as shown on screen, unsaved changes included, as Excel
                or CSV, optionally through an export layout. Nothing is kept — the same as Export in Salesforce.
              </p>
            </HelpPopover>
            <Button
              type="button"
              size="sm"
              disabled={viewId === null}
              aria-describedby={viewId === null ? runReason : undefined}
              onClick={() => setRunning(true)}
            >
              Run now…
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setExporting(true)}>
              Export now…
            </Button>
            {viewSaves.menu}
            {view?.migratedFrom && <MigratedChip view={view} />}
          </>
        }
      />
      <div className="grid min-w-0 gap-4 px-6 py-6">
        {viewId === null && (
          <p id={runReason} className="text-sm text-muted-foreground">
            A new report of {listSurveyLabel(survey)} — choose its fields and conditions, then Save → Save as…
            to keep it and run it.
          </p>
        )}
        {viewSaves.notice}
        <Outlet />
      </div>

      {viewId !== null && (
        <RunNowDialog
          open={running}
          onOpenChange={setRunning}
          viewId={viewId}
          changed={changed}
          runs={runs}
          sayProblem={(problem) => sayProblem(problem, query, fieldOf)}
          onAsked={() => {
            if (!location.pathname.endsWith('/runs')) navigate(reportPath(viewId, 'runs'), { state: location.state });
          }}
        />
      )}
      <ExportDialog
        open={exporting}
        onClose={() => setExporting(false)}
        survey={survey}
        query={exportQuery}
        shownColumns={columns.filter((code) => fields.has(code)).length}
        total={null}
      />
      <SaveAsReportDialog
        open={bridging}
        onClose={() => setBridging(false)}
        survey={survey}
        query={query}
        to="list"
        formattingRules={rules}
      />
    </ReportContext.Provider>
  );
}

/** The header's line: who sees the report — Lists' icon, then Shared · elena@veritant.com. */
function ReportFacts({ view }: { view: Nullable<ISavedView> }) {
  if (view === null) return <span>Not saved yet</span>;
  return (
    <span className="inline-flex items-center gap-1.5" data-view={view.id}>
      <VisibilityIcon visibility={view.visibility} />
      {visibilityLabel(view.visibility)} · {viewOwner(view)}
    </span>
  );
}
